'use client';
import BDTitle from '@shared/components/elements/title';
import { HSection } from '@shared/components/home/section';
import { Cart } from '@shared/lib/shopify/types/shopify-native';
import {
  groupJewelryWithDiamonds,
  groupLinesInCart,
  isCartEmpty,
} from '@shared/lib/utils/cart-utils';
import { addMoney, renderMoney, zeroMoney } from '@shared/lib/utils/money';
import Link from 'next/link';
import InCartListItem from '@shared/components/shop/diamonds/in-cart-list-item';
import PriceSummaryCheckout from './checkout';

export default function CartSummary({
  cart,
  isQuote = false,
  quoteCart,
}: {
  cart: Cart;
  isQuote?: boolean;
  quoteCart?: Cart;
}) {
  if (isCartEmpty(cart)) {
    return <></>;
  }
  const { kit, premiumProcessing, diamonds, inStockDiamonds, jewelry, others } =
    groupLinesInCart(cart);
  const kitCost = kit?.reduce((acc, line) => {
    return addMoney(acc, line.cost.totalAmount);
  }, zeroMoney);
  const premiumProcessingCost = premiumProcessing?.reduce((acc, line) => {
    return addMoney(acc, line.cost.totalAmount);
  }, zeroMoney);

  const { jewelries, looseBondedDiamonds, looseInStockDiamonds } = groupJewelryWithDiamonds(
    diamonds || [],
    inStockDiamonds || [],
    jewelry || [],
    cart
  );
  const serviceLines = [...kit, ...premiumProcessing];

  return (
    <HSection>
      <BDTitle>{isQuote ? 'Your Quote' : "Let's review your order"}</BDTitle>
      <div className="md:grid md:grid-cols-5 md:items-start">
        <section
          key="cart-lists"
          aria-labelledby="cart-lists"
          className="flex flex-col gap-8 md:col-span-3 md:pr-4"
        >
          {/* Service Charges */}
          {(kit.length > 0 || premiumProcessing.length > 0) && (
            <section aria-labelledby="service-charges-heading">
              <h2 className="text-xl font-normal uppercase">
                {premiumProcessingCost && kitCost
                  ? `Total service charge ${renderMoney(addMoney(premiumProcessingCost, kitCost))}.*`
                  : premiumProcessingCost
                    ? `Premium processing fee ${renderMoney(premiumProcessingCost)}.`
                    : kitCost
                      ? `Refundable get started kit`
                      : ''}
              </h2>
              <Link href="/shop/service" className="text-bd-primary text-sm">
                Learn more about the get started kit.
              </Link>
              <ul role="list" className="mt-4 flex flex-col gap-4 divide-x divide-gray-200">
                <InCartListItem
                  key="service"
                  lineItem={kit![0]!}
                  operatingCart={cart!}
                  additionalInfo={
                    <p className="text-sm">
                      Get started kit will be refunded at order confirmation.
                    </p>
                  }
                  canRemove={false}
                  canUpdate={false}
                />
              </ul>
            </section>
          )}
          {/* Jewelry Charges */}
          {jewelries.length > 0 && (
            <section aria-labelledby="jewelry-charges-heading">
              <h2 className="text-xl font-normal uppercase">Jewelry with Bonded Diamond™</h2>

              <ul role="list" className="mt-4 flex flex-col gap-4 divide-x divide-gray-200">
                {jewelries.map(({ jewelry, diamonds }) => (
                  <InCartListItem
                    key={jewelry.id}
                    lineItem={jewelry}
                    secondaryLineItem={diamonds[0]}
                    operatingCart={cart!}
                    canRemove={true}
                    canUpdate={false}
                  />
                ))}
              </ul>
            </section>
          )}
          {/* Diamond Charges */}
          {looseBondedDiamonds.length > 0 && (
            <section aria-labelledby="cart-item-charges-header">
              <h2 className="text-xl font-normal uppercase">
                {`${looseBondedDiamonds.length > 0 ? 'Bonded Diamonds™' : 'Bonded Diamond™'} with no jewelry set`}
              </h2>

              <ul role="list" className="mt-4 flex flex-col gap-4 divide-x divide-gray-200">
                {looseBondedDiamonds.map((diamond) => (
                  <InCartListItem
                    key={diamond.id}
                    lineItem={diamond}
                    operatingCart={cart}
                    canRemove={true}
                    canUpdate={true}
                  />
                ))}
              </ul>
            </section>
          )}
          {looseInStockDiamonds.length > 0 && (
            <section aria-labelledby="cart-item-charges-header">
              <h2 className="text-xl font-normal uppercase">
                In Stock Diamond{looseInStockDiamonds.length > 1 ? 's' : ''} with no jewelry set
              </h2>
              <ul role="list" className="mt-4 flex flex-col gap-4 divide-x divide-gray-200">
                {looseInStockDiamonds.map((diamond) => (
                  <InCartListItem
                    key={diamond.id}
                    lineItem={diamond}
                    operatingCart={cart}
                    canRemove={true}
                    canUpdate={true}
                  />
                ))}
              </ul>
            </section>
          )}
          {others && others.length > 0 && (
            <section aria-labelledby="others-charges-heading">
              <h2 className="text-xl font-normal uppercase">Other items</h2>
              <ul role="list" className="mt-4 flex flex-col gap-4 divide-x divide-gray-200">
                {others.map((other) => (
                  <InCartListItem
                    key={other.id}
                    lineItem={other}
                    operatingCart={cart}
                    canRemove={true}
                    canUpdate={true}
                  />
                ))}
              </ul>
            </section>
          )}
        </section>

        {/* Order summary */}
        <section
          key="price-summary"
          className="sticky mt-8 flex w-full flex-col gap-4 md:col-span-2 md:mt-0 md:pl-4"
        >
          <PriceSummaryCheckout
            cart={cart}
            isQuote={isQuote}
            quoteCart={quoteCart}
            serviceLines={serviceLines}
            jewelries={jewelries}
            looseBondedDiamonds={looseBondedDiamonds}
            looseInStockDiamonds={looseInStockDiamonds}
            others={others || []}
          />
        </section>
      </div>
    </HSection>
  );
}
